import { useEffect, type FunctionComponent } from "react";
import { useQuery } from "@tanstack/react-query";
import { Form, Input, Radio, Select } from "antd";
import { type FormInstance } from "antd/lib/form";
import { useTranslation } from "react-i18next";

import { ChallengeStatus } from "@fitness-app/data-models/entities/Challenge";

import { supabase } from "~/store/initializeStore";

import { ScheduleTimingFields } from "../ScheduleTimingFields/ScheduleTimingFields";
import { TimeField } from "../TimeField/TimeField";
import { type EnrollChallengeTaskFormModel } from "./types";

interface OwnProps {
  formController?: FormInstance<EnrollChallengeTaskFormModel>;
  onSubmit: (formData: EnrollChallengeTaskFormModel) => void;
  // Partial: package-add passes only a scheduleTiming preset, not a full model.
  model?: Partial<EnrollChallengeTaskFormModel> | null;
}

type Props = OwnProps;

const EnrollChallengeTaskForm: FunctionComponent<Props> = ({ formController, onSubmit, model }) => {
  const { t } = useTranslation(["automation", "common"]);

  // Recurring templates only — joining "the current edition" makes sense solely for
  // templates whose editions the scheduler keeps spawning (recurrence != null).
  const challengeTemplatesQuery = useQuery({
    queryKey: ["automation-challenge-templates"],
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("challenge_template")
        .select("id, name")
        .eq("archived", false)
        .not("recurrence", "is", null)
        .order("name")
        .returns<{ id: string; name: string }[]>();

      if (error) {
        throw error;
      }

      return data ?? [];
    },
  });

  const challengesQuery = useQuery({
    queryKey: ["automation-challenges"],
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("challenge")
        .select("id, name, startDate, status")
        .in("status", [ChallengeStatus.Upcoming, ChallengeStatus.Active])
        .order("startDate", { ascending: false })
        .returns<{ id: string; name: string; startDate: string; status: string }[]>();

      if (error) {
        throw error;
      }

      return data ?? [];
    },
  });

  useEffect(() => {
    // reset-then-apply so a preset (or edited task) never leaves stale values
    // from a previous open in the shared form controller.
    formController?.resetFields();
    if (model) {
      formController?.setFieldsValue(model);
    }
  }, [model, formController]);

  return (
    <div>
      <Form<EnrollChallengeTaskFormModel>
        name="enroll-challenge-task-form"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 14 }}
        layout="horizontal"
        form={formController}
        initialValues={{
          name: "",
          targetMode: "template",
          challengeTemplateId: null,
          challengeId: null,
          sentTimeConfig: { mode: "absolute", avoidSendingOnTheNight: true },
          ...model,
        }}
        onFinish={onSubmit}
      >
        <Form.Item
          name="name"
          label="Nazwa"
          rules={[
            {
              required: true,
              message: t<string>("common:validationErrors.fieldIsRequired"),
            },
          ]}
        >
          <Input placeholder="np. Dołączenie do wyzwania startowego" />
        </Form.Item>

        <Form.Item
          name="targetMode"
          label="Cel zapisu"
          rules={[
            {
              required: true,
              message: t<string>("common:validationErrors.fieldIsRequired"),
            },
          ]}
        >
          <Radio.Group>
            <Radio value="template">Bieżąca edycja cyklicznego wyzwania</Radio>
            <Radio value="challenge">Konkretne wyzwanie</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item noStyle dependencies={["targetMode"]}>
          {({ getFieldValue }) =>
            getFieldValue("targetMode") === "template" ? (
              <Form.Item
                name="challengeTemplateId"
                label="Szablon wyzwania"
                rules={[
                  {
                    required: true,
                    message: t<string>("common:validationErrors.fieldIsRequired"),
                  },
                ]}
              >
                <Select
                  loading={challengeTemplatesQuery.isLoading}
                  placeholder="Wybierz szablon cyklicznego wyzwania"
                  optionFilterProp="label"
                  showSearch
                  options={(challengeTemplatesQuery.data ?? []).map((template) => ({
                    value: template.id,
                    label: template.name,
                  }))}
                />
              </Form.Item>
            ) : (
              <Form.Item
                name="challengeId"
                label="Wyzwanie"
                rules={[
                  {
                    required: true,
                    message: t<string>("common:validationErrors.fieldIsRequired"),
                  },
                ]}
              >
                <Select
                  loading={challengesQuery.isLoading}
                  placeholder="Wybierz wyzwanie (nadchodzące lub aktywne)"
                  optionFilterProp="label"
                  showSearch
                  options={(challengesQuery.data ?? []).map((challenge) => ({
                    value: challenge.id,
                    label: `${challenge.name} (od ${challenge.startDate})`,
                  }))}
                />
              </Form.Item>
            )
          }
        </Form.Item>

        <TimeField showOnlyAbsolute={false} label={t<string>("automation:automatedMessageForm.operationTime")} />

        <ScheduleTimingFields formController={formController} />
      </Form>
    </div>
  );
};

export default EnrollChallengeTaskForm;
